import { TWidget } from 'widgets/Widget';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';

import { TButtonInstance } from 'widgets/global/Button';

/**
 * @description Base ProductTile implementation
 * @param Widget Base widget for extending
 * @returns Product Tile widget
 */
function ProductTileClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory product
     * @class ProductTile
     * @augments Widget
     * @classdesc Product Tile component. Used in different product grids.
     * @property {string} data-widget - Widget name `productTile`
     * @property {string} data-pid - product ID
     * @property {string} data-product-name - product name
     * @property {object} data-analytics - analytics data for product tile
     * @property {string} data-text-network-error - network error text, when current ajax operation ended up with network error
     * @property {string} data-text-added-to-wishlist - a text which is used to indicate, that product has being added to wishlist
     * @property {object} data-accessibility-alerts - contains accessibility alerts for product tile actions
     * @property {object} data-grid-item - indicates, that product tile is used inside product grid
     *
     * @example <caption>Product Tile widget usage</caption>
     * <section
     *     class="b-product_tile"
     *     aria-label="${product.productName}"
     *     <isif condition="${pdict.first}">
     *         data-id="firstTile"
     *     </isif>
     *     data-pid="${product.id}"
     *     data-product-name="${product.productName}"
     *     data-product-options="${JSON.stringify(product.options)}"
     *     data-product-id="${product.id}"
     *     data-widget="productTile"
     *     data-analytics="${JSON.stringify(product.gtmInfo)}"
     *     data-recommendation="true"
     *     data-text-network-error="${Resource.msg('error.alert.network', 'product', null)}"
     *     data-text-added-to-wishlist="${Resource.msg('button.added.to.wishlist', 'wishlist', null)}"
     *     data-show-alert-if-product-in-wishlist="true"
     *     data-accessibility-alerts='{
     *         "removedfromwishlist": "${Resource.msg('alert.removedfromwishlist', 'product', null)}",
     *         "addedtowishlist": "${Resource.msg('alert.addedtowishlist', 'product', null)}"
     *     }'
     *     data-grid-item=true
     *     data-tau="product_tile"
     *     data-tau-product-id="${product.id}"
     * >
     *     <isinclude template="product/productTile" />
     * </section>
     */
    class ProductTile extends Widget {
        prefs() {
            return {
                productName: '',
                gridItem: false,
                ...super.prefs()
            };
        }

        /**
         * @description Init widget
         * @emits "product.tile.init"
         */
        init() {
            super.init();

            this.eventBus().emit('product.tile.init', this.ref('self'));
        }

        /**
         * @description Executes when user clicks on product details link on product tile.
         * Usually used by analytics etc.
         * @emits tile.product.link.click
         * @param link - product link
         */
        onProductLinkClick(link: TRefElementInstance) {
            this.eventBus().emit('tile.product.link.click', link);
        }

        /**
         * @description On modal window show callback
         * @emits "product.tile.qv.open"
         */
        onModalShow() {
            this.eventBus().emit('product.tile.qv.open', {
                gtmInfo: this.data('analytics')
            });
        }

        showQuickView(button: TButtonInstance) {
            /**
             * @description Event dispatched, when Global Modal should be triggered
             * @event module:events#dialogshow
             */
            this.eventBus().emit('dialogshow', {
                url: button.data('url'),
                wrapperClasses: 'm-quick_view',
                ariaLabel: this.prefs().productName,
                attributes: {
                    'data-tau-unique': 'quick_view_dialog'
                }
            }, this.onModalShow.bind(this));
        }

        /**
         * @description Focus
         */
        focus() {
            this.ref('tileImageLink').focus();
        }
    }

    return ProductTile;
}

export type TProductTile = ReturnType<typeof ProductTileClassCreator>;

export type TProductTileInstance = InstanceType<TProductTile>;

export default ProductTileClassCreator;
